import React, { useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import { PictoListingSvg, PictoSliderSvg } from '../../assets/svg';
import theme from '../../styled-components/dark';
import { NewTag } from '../../styled-components/shared';
import type { PresentationItem } from '../../types/betGroup';
import { isBetPresentationModeSlider } from '../../utils/bet';
import BetCurrentTotalTagContainer from '../BetCurrentTotalTag/BetCurrentTotalTagContainer';
import FadeTransitionGroup from '../FadeTransitionGroup';
import HelperButton from '../HelperButton';
import Tabs from '../Tabs';

const BetGroupHeader = styled.div`
  position: relative;
  width: 100%;
  font-weight: normal;
`;

const Title = styled.div``;

const StyledHelperButton = styled(HelperButton)`
  font-size: 0.7em;
  flex-shrink: 0;
`;

const HelperText = styled.p`
  font-size: 0.9em;
  padding: 0.4em;
  margin: 0;
  color: ${theme.Match.HelperText.color};
  white-space: pre-wrap;
`;

const Separator = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.1em;
  padding: 0.25em 0.6em;
  min-height: 2em;
`;

const LeftBloc = styled.span`
  display: flex;
  align-items: center;
  text-transform: none;
  font-size: 1.1em;
  font-weight: 600;
  color: ${theme.Match.GroupHeader.color};
  line-height: 1.1em;
  gap: 0.5em;
`;

const RightBloc = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
`;

const BetPresentationModeTabs = styled(Tabs)`
  margin-left: 0.1em;
`;

const sharedPictoStyles = css`
  padding: 0.2em;
  width: 1.5em;
  height: 1.5em;
`;

const StyledPictoListing = styled(PictoListingSvg)`
  ${sharedPictoStyles}
`;
const StyledPictoSlider = styled(PictoSliderSvg)`
  ${sharedPictoStyles}
`;

const StyledBetCurrentTotalTagContainer = styled(BetCurrentTotalTagContainer)`
  font-size: 0.6em;
`;

type MatchGroupHeaderProps = {
  title: string;
  helperText: string;
  forwardedRef?: unknown;
  betPresentationModes: Array<PresentationItem>;
  selectedPresentationMode: number;
  setBetPresentationMode: (idx: number) => void;
  showNewTag?: boolean;
  className?: string;
  betId: number;
};

const MatchGroupHeader = React.memo(
  React.forwardRef<HTMLDivElement, MatchGroupHeaderProps>(function MatchGroupHeader(
    {
      title,
      helperText,
      betPresentationModes,
      selectedPresentationMode,
      setBetPresentationMode,
      showNewTag,
      betId,
      className,
    }: MatchGroupHeaderProps,
    forwardedRef,
  ) {
    const [shouldShowHelper, setShouldShowHelper] = useState(false);

    const toggleHelper = useCallback(() => {
      setShouldShowHelper(!shouldShowHelper);
    }, [shouldShowHelper]);

    const tabs = useMemo(
      () =>
        betPresentationModes.map((mode, index) => ({
          id: index,
          icon: !isBetPresentationModeSlider(mode.type) ? <StyledPictoListing /> : <StyledPictoSlider />,
        })),
      [betPresentationModes],
    );
    const shouldDisplay = useMemo(
      () => betPresentationModes?.length > 1 || title,
      [betPresentationModes?.length, title],
    );

    const onBetPresentationChange = useCallback(
      (idx: number | string) => {
        if (typeof idx === 'number') {
          setBetPresentationMode(idx);
        }
      },
      [setBetPresentationMode],
    );

    const displayRightBloc = useMemo(() => betPresentationModes.length > 1, [betPresentationModes.length]);

    return (
      <BetGroupHeader className={className} ref={forwardedRef}>
        {shouldDisplay ? (
          <Separator>
            <LeftBloc>
              <Title>{title}</Title>
              <StyledBetCurrentTotalTagContainer betId={betId} />
              {helperText ? <StyledHelperButton toggled={shouldShowHelper} onToggle={toggleHelper} /> : null}
              {showNewTag ? <NewTag>New</NewTag> : null}
            </LeftBloc>
            {displayRightBloc ? (
              <RightBloc>
                <BetPresentationModeTabs
                  tabs={tabs}
                  selected={selectedPresentationMode}
                  onSelectionChange={onBetPresentationChange}
                  customStyle={{
                    defaultBgColor: theme.DefaultTabs.default.backgroundColor,
                    defaultColor: theme.DefaultTabs.default.color,
                    selectedBgColor: theme.DefaultTabs.selected.backgroundColor,
                    selectedColor: theme.DefaultTabs.selected.color,
                  }}
                />
              </RightBloc>
            ) : null}
          </Separator>
        ) : null}
        <FadeTransitionGroup>{shouldShowHelper ? <HelperText>{helperText}</HelperText> : null}</FadeTransitionGroup>
      </BetGroupHeader>
    );
  }),
);

export default MatchGroupHeader;
